import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { LuCircleHelp } from "react-icons/lu";
import SearchBar from './component/Help/Searchbar';
import Fuse from 'fuse.js';

const helpData = [
  {
    id: 1,
    question: 'How do I reset my password?',
    meta: 'Instructions for resetting a forgotten password.',
    createdAt: '2022-01-01',
    Ans: <div>Follow these steps to reset your password...</div>,
  },
  {
    id: 2,
    question: 'Where can I find my purchase history?',
    meta: 'Guide to finding your previous transactions and purchases.',
    createdAt: '2022-01-05',
    Ans: <div>Your purchase history is located in...</div>,
  },
  {
    id: 3,
    question: 'How do I update my profile information?',
    meta: 'Updating your personal and contact information.',
    createdAt: '2022-01-10',
    Ans: <div>To update your profile information...</div>,
  },
  {
    id: 4,
    question: 'What is the refund policy?',
    meta: 'Understanding the terms for returning products and getting refunds.',
    createdAt: '2022-01-15',
    Ans: <div>Our refund policy states that...</div>,
  },
  {
    id: 5,
    question: 'How do I contact customer support?',
    meta: 'Various ways to get in touch with our support team.',
    createdAt: '2022-01-20',
    Ans: <div>You can contact customer support by...</div>,
  },
  {
    id: 6,
    question: 'Is my data secure?',
    meta: 'Information on how we protect your data and privacy.',
    createdAt: '2022-01-25',
    Ans: <div>We ensure your data is secure by...</div>,
  },
  {
    id: 7,
    question: 'How to change notification settings?',
    meta: 'Managing the frequency and types of notifications you receive.',
    createdAt: '2022-02-01',
    Ans: <div>To change your notification settings...</div>,
  },
  {
    id: 8,
    question: 'Can I share my account with others?',
    meta: 'Policy on account sharing and multiple users.',
    createdAt: '2022-02-05',
    Ans: <div>Our policy on account sharing is...</div>,
  },
  {
    id: 9,
    question: 'How to cancel a subscription?',
    meta: 'Steps to cancel your ongoing subscription services.',
    createdAt: '2022-02-10',
    Ans: <div>To cancel your subscription...</div>,
  },
  {
    id: 10,
    question: 'What are the system requirements?',
    meta: 'Minimum and recommended system specs for optimal usage.',
    createdAt: '2022-02-15',
    Ans: <div>The system requirements are...</div>,
  },
];

const Help = () => {
  const data = helpData;

  const [currentPage, setCurrentPage] = useState(1);
  const [searchResults, setSearchResults] = useState(data);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentItems = searchResults.slice(firstIndex, lastIndex);

  const truncateText = (text, maxLength) =>
    text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

  const goToPage = (pageNum) => {
    if (pageNum < 1) setCurrentPage(1);
    else if (pageNum > totalPages) setCurrentPage(totalPages);
    else setCurrentPage(pageNum);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPage(currentPage - 1);
      } else if (e.key === 'ArrowRight') {
        goToPage(currentPage + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const checkAspectRatio = () => {
      const aspectRatio = window.innerWidth / window.innerHeight;
      if (window.innerHeight <= 600) {
        setItemsPerPage(4);
      } else if (window.innerHeight <= 800) {
        setItemsPerPage(6);
      } else if (window.innerHeight <= 1000) {
        setItemsPerPage(8);
      } else {
        setItemsPerPage(10);
      }
      // setItemsPerPage(aspectRatio < 1 ? 6 : 4); // Display more items if height > width
    };

    checkAspectRatio(); // Check on component mount
    window.addEventListener('resize', checkAspectRatio); // Adjust on window resize
    return () => window.removeEventListener('resize', checkAspectRatio);
  }, []);

  const fuse = new Fuse(data, {
    keys: ['question', 'meta'],
    includeScore: true,
  });

  const handleSearch = (query) => {
    if (query) {
      setSearchResults(fuse.search(query).map((result) => result.item));
    } else {
      setSearchResults(data);
    }
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col p-4 md:p-10 max-w-6xl w-full mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground">
          <LuCircleHelp className="w-5 h-5" />
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground">
          Help Center
        </h1>
      </div>

      <SearchBar onSearch={handleSearch} />

      {/* List */}
      <div className="space-y-2">
        {currentItems.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No results found.
          </p>
        )}
        {currentItems.map((item) => (
          <button
            key={item.id}
            to={`/help/${item.id}`}
            className="cursor-not-allowed w-full text-left block rounded-lg border border-border bg-card p-4 hover:bg-accent/50 transition-colors"
            disabled
          >
            <h2 className="font-display font-semibold text-card-foreground text-sm md:text-base">
              {item.question}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {item.meta}
            </p>
          </button>
        ))}
      </div>

        <div className="flex justify-between items-center gap-4 mt-4">
          {/* Previous Button */}
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Previous Page"
          >
            <FiChevronLeft className="w-5 h-5 dark:text-white" />
          </button>

          <span className="dark:text-white">
            Page {currentPage} of {totalPages}
          </span>

        {/* Next Button */}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next Page"
        >
          <FiChevronRight className="w-5 h-5 dark:text-white" />
        </button>
      </div>
    </div>
  );
};

export default Help;
